// AuthContext — manages Supabase auth state + the profile row.
// IMPORTANT: Supabase v2 deadlock prevention — NEVER `await` async work
// inside the onAuthStateChange listener; fire-and-forget instead.
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "../lib/supabase";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  // Recovery flag — true when Supabase fires PASSWORD_RECOVERY. Persisted in
  // sessionStorage so a full-page reload on /reset-password keeps the guard
  // active. Cleared only after the user successfully updates their password.
  const [recoveryRequired, setRecoveryRequired] = useState(() => {
    try { return sessionStorage.getItem("gg_password_recovery") === "1"; }
    catch { return false; }
  });
  const lastFetchedUserId = useRef(null);

  const fetchProfile = useCallback(async (userId) => {
    if (!userId) return null;
    try {
      const { data, error } = await supabase
        .from("users")
        .select("*")
        .eq("id", userId)
        .maybeSingle();
      if (error) {
        console.error("[auth] fetchProfile error", error);
        return null;
      }
      if (!data) {
        // Trigger may not have populated yet (rare). Create it ourselves.
        const { data: u } = await supabase.auth.getUser();
        const email = u?.user?.email;
        const name = u?.user?.user_metadata?.name || (email ? email.split("@")[0] : "");
        const { data: inserted } = await supabase
          .from("users")
          .insert({ id: userId, email, name })
          .select()
          .maybeSingle();
        setProfile(inserted || null);
        return inserted;
      }
      setProfile(data);
      return data;
    } catch (e) {
      console.error("[auth] fetchProfile threw", e);
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    // Initial session load — wrapped so loading is ALWAYS released.
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(data.session);
        if (data.session?.user) {
          lastFetchedUserId.current = data.session.user.id;
          // Fire-and-forget; do NOT block loading on this.
          fetchProfile(data.session.user.id);
        }
      } catch (e) {
        console.error("[auth] getSession failed", e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    // Listen for future auth state changes.
    // CRITICAL: do not `await` inside this callback (Supabase v2 deadlock).
    const { data: sub } = supabase.auth.onAuthStateChange((event, sess) => {
      // Password-recovery flow: Supabase v2 delivers the recovery access token
      // BEFORE any other auth change. We stash a flag so route guards can
      // force the user through /reset-password even though a session exists.
      if (event === "PASSWORD_RECOVERY") {
        try { sessionStorage.setItem("gg_password_recovery", "1"); } catch { /* ignore */ }
        setRecoveryRequired(true);
      }
      setSession(sess);
      if (sess?.user) {
        // Only refetch if this is a DIFFERENT user from what we've already loaded,
        // OR if we don't have a profile yet. Avoid spurious re-fetches.
        if (lastFetchedUserId.current !== sess.user.id) {
          lastFetchedUserId.current = sess.user.id;
          fetchProfile(sess.user.id);
        }
      } else {
        lastFetchedUserId.current = null;
        setProfile(null);
        // Session gone → recovery no longer applies.
        try { sessionStorage.removeItem("gg_password_recovery"); } catch { /* ignore */ }
        setRecoveryRequired(false);
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signUp = async ({ email, password, name }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name } },
    });
    return { data, error };
  };

  const signIn = async ({ email, password }) => {
    return supabase.auth.signInWithPassword({ email, password });
  };

  const signInWithGoogle = async () => {
    const redirectTo = `${window.location.origin}/app`;
    return supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo },
    });
  };

  const sendPasswordReset = async (email) => {
    const redirectTo = `${window.location.origin}/reset-password`;
    return supabase.auth.resetPasswordForEmail(email, { redirectTo });
  };

  const updatePassword = async (newPassword) => {
    const res = await supabase.auth.updateUser({ password: newPassword });
    if (!res.error) {
      // Recovery flow complete — clear the guard so protected routes open up.
      try { sessionStorage.removeItem("gg_password_recovery"); } catch { /* ignore */ }
      setRecoveryRequired(false);
    }
    return res;
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setSession(null);
    lastFetchedUserId.current = null;
    try { sessionStorage.removeItem("gg_password_recovery"); } catch { /* ignore */ }
    setRecoveryRequired(false);
  };

  const refreshProfile = useCallback(async () => {
    // Prefer in-memory session, but fall back to a fresh getSession()
    // because onAuthStateChange is asynchronous — right after signIn returns,
    // our `session` state may still be null while Supabase already has it.
    let userId = session?.user?.id;
    if (!userId) {
      const { data } = await supabase.auth.getSession();
      userId = data.session?.user?.id;
    }
    if (!userId) return null;
    return fetchProfile(userId);
  }, [session, fetchProfile]);

  const value = {
    session,
    user: session?.user || null,
    profile,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    sendPasswordReset,
    updatePassword,
    signOut,
    refreshProfile,
    setProfile,
    recoveryRequired,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
